import { memo, useRef, useEffect } from 'react'
import { Download, Crown, Sparkles, MoreHorizontal } from 'lucide-react'
import { Tooltip } from '@/components/ui'
import { cn } from '@/lib/utils'

interface CreatorFeaturesMenuProps {
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
  hasCreatorAccess: boolean
  onDownloadNoWatermark: () => void
  onDispute?: () => void // Optional - dispute feature disabled
  onUpgrade: () => void
}

/**
 * "More Options" dropdown menu for creator-only features.
 * Shows Download without Watermark and Dispute Generation options.
 */
export const CreatorFeaturesMenu = memo(function CreatorFeaturesMenu({
  isOpen,
  onToggle,
  onClose,
  hasCreatorAccess,
  onDownloadNoWatermark,
  // onDispute,
  onUpgrade,
}: CreatorFeaturesMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  return (
    <div ref={menuRef} className="relative">
      <Tooltip title="More Options" position="top" align="center">
        <button
          onClick={onToggle}
          className="p-2 rounded-md transition-colors text-[#666] hover:text-white hover:bg-white/[0.05]"
        >
          <MoreHorizontal className="size-[18px]" />
        </button>
      </Tooltip>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-[200px] xl:w-64 bg-[#111] border border-white/[0.1] rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Header - only show for non-creator users */}
          {!hasCreatorAccess && (
            <div className="px-3 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
              <Sparkles className="size-3.5 text-violet-400" />
              <span className="text-xs font-medium text-[#888]">Creator Features</span>
            </div>
          )}

          {/* Download without Watermark */}
          <button
            onClick={onDownloadNoWatermark}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left"
          >
            <div className={cn(
              "p-1.5 rounded-md shrink-0",
              hasCreatorAccess ? "bg-white/[0.05]" : "bg-violet-500/10"
            )}>
              <Download className={cn("size-3.5", hasCreatorAccess ? "text-white/70" : "text-violet-400")} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-white whitespace-nowrap">
                <span className="xl:hidden">No Watermark</span>
                <span className="hidden xl:inline">Download (No Watermark)</span>
              </span>
              {!hasCreatorAccess && <Crown className="size-3 text-violet-400 inline ml-1.5" />}
            </div>
          </button>

          {/* Dispute Generation - commented out */}
          {/* <button
            onClick={onDispute}
            className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left border-t border-white/[0.06]"
          >
            <div className={cn(
              "p-1.5 rounded-md shrink-0",
              hasCreatorAccess ? "bg-white/[0.05]" : "bg-amber-500/10"
            )}>
              <AlertCircle className={cn("size-3.5", hasCreatorAccess ? "text-white/70" : "text-amber-400")} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-white whitespace-nowrap">Dispute Generation</span>
              {!hasCreatorAccess && <Crown className="size-3 text-violet-400 inline ml-1.5" />}
              <p className="text-[11px] text-[#555] mt-0.5 whitespace-nowrap">Refund • 7 day review</p>
            </div>
          </button> */}

          {/* Upgrade Notice */}
          {!hasCreatorAccess && (
            <button
              onClick={onUpgrade}
              className="w-full px-3 py-2 bg-violet-500/5 border-t border-violet-500/10 hover:bg-violet-500/10 transition-colors"
            >
              <p className="text-[11px] text-violet-300/80 text-center">
                Upgrade to <span className="font-medium text-violet-300">Creator</span> to unlock
              </p>
            </button>
          )}
        </div>
      )}
    </div>
  )
})
